// Match-scoring shared between the renter Profile, Browse, PropertyDetail,
// Favorites and RenterInbox pages, and mirrored server-side in the
// notify-matching-renters edge function.
//
// Scoring is a WEIGHTED fractional average: each criterion the renter expressed
// contributes `weight` to the denominator and `weight * f` to the numerator,
// where f∈[0,1] is how well the listing satisfies it (amenities/services/dates
// are fractional, the rest are 0/1). The per-criterion weights are tunable by
// admins (stored in match_config); DEFAULT_WEIGHTS reproduces sensible defaults
// with budget weighted heaviest.
//
// On top of the soft score, a renter can mark preferences as DEAL-BREAKERS
// (hard requirements), if any fails, the listing scores 0 (excluded).
import { eachMonthOfInterval, format, parseISO } from "date-fns";
import type { Property } from "@/hooks/useProperties";

export interface RenterPreferences {
  budgetMin?: number | null;
  budgetMax?: number | null;
  preferredAreas?: string[] | null;
  preferredTypes?: string[] | null;
  preferredBedrooms?: number | null;
  preferredBathrooms?: number | null;
  preferredAmenities?: string[] | null;
  preferredServices?: string[] | null;
  moveInDate?: Date | null;
  moveOutDate?: Date | null;
  numPeople?: number | null;
  hasPets?: boolean | null;
  /** Deal-breaker tokens, see DEALBREAKER token format in matchScore. */
  dealbreakers?: string[] | null;
}

export interface MatchWeights {
  budget: number;
  area: number;
  type: number;
  bedrooms: number;
  bathrooms: number;
  guests: number;
  pets: number;
  amenities: number;
  services: number;
  dates: number;
}

// Budget weighted heaviest by default; area/dates next; the rest equal.
export const DEFAULT_WEIGHTS: MatchWeights = {
  budget: 3,
  area: 2,
  dates: 2,
  bedrooms: 1,
  bathrooms: 1,
  guests: 1,
  type: 1,
  pets: 1,
  amenities: 1,
  services: 1,
};

export const DEFAULT_THRESHOLD = 70;

// Order matters only for the admin UI; keep in sync with MatchWeights keys.
export const WEIGHT_KEYS: (keyof MatchWeights)[] = [
  "budget", "area", "dates", "bedrooms", "bathrooms",
  "guests", "type", "pets", "amenities", "services",
];

export const WEIGHT_LABELS: Record<keyof MatchWeights, string> = {
  budget: "Budget",
  area: "Area",
  dates: "Dates / availability",
  bedrooms: "Bedrooms",
  bathrooms: "Bathrooms",
  guests: "Guests",
  type: "Property type",
  pets: "Pet friendly",
  amenities: "Amenities",
  services: "Services",
};

const SERVICE_KEY_MAP: Record<string, keyof Property["services"]> = {
  Cleaning: "cleaning",
  Electricity: "electricity",
  Gardener: "gardener",
  Internet: "internet",
  "Pool Manager": "poolManager",
  "Property Manager": "propertyManager",
  Water: "water",
};

const propertyAmenitiesLower = (p: Property): string[] =>
  [
    ...(p.amenities || []),
    p.pool ? "Pool" : "",
    p.oceanView ? "Ocean View" : "",
    p.petFriendly ? "Pet Friendly" : "",
    p.ac ? "A/C" : "",
    p.internet ? "WiFi" : "",
    p.parking ? "Parking" : "",
    p.security ? "Security" : "",
  ]
    .filter(Boolean)
    .map((a) => a.toLowerCase());

const hasService = (p: Property, label: string): boolean => {
  const key = SERVICE_KEY_MAP[label];
  return !!(key && p.services[key]);
};

// --- per-criterion pass tests (reused by score + deal-breaker gate) ---
const budgetPass = (p: Property, prefs: RenterPreferences): boolean => {
  const inMin = !prefs.budgetMin || p.price >= prefs.budgetMin;
  const inMax = !prefs.budgetMax || p.price <= prefs.budgetMax;
  return inMin && inMax;
};

const areaPass = (p: Property, areas: string[]): boolean =>
  areas.some((a) => p.location?.toLowerCase().includes(a.toLowerCase()));

const typePass = (p: Property, types: string[]): boolean =>
  types.map((t) => t.toLowerCase()).includes(p.type?.toLowerCase());

const requestedMonths = (prefs: RenterPreferences): string[] =>
  prefs.moveInDate && prefs.moveOutDate
    ? eachMonthOfInterval({ start: prefs.moveInDate, end: prefs.moveOutDate }).map((d) =>
        format(d, "yyyy-MM"),
      )
    : [];

// Fraction of requested months the listing is available (1 = fully available).
const datesFraction = (p: Property, prefs: RenterPreferences): number => {
  if (prefs.moveInDate && prefs.moveOutDate) {
    const months = requestedMonths(prefs);
    const propMonths = (p.available || []) as string[];
    if (propMonths.length > 0) {
      const exact = months.filter((m) => propMonths.includes(m)).length;
      return months.length ? exact / months.length : 0;
    }
    if (p.availableDate) return prefs.moveInDate >= parseISO(p.availableDate) ? 1 : 0;
    return 0;
  }
  if (prefs.moveInDate && p.availableDate) {
    return prefs.moveInDate >= parseISO(p.availableDate) ? 1 : 0;
  }
  return 0;
};

const hasDatePref = (prefs: RenterPreferences): boolean =>
  !!((prefs.moveInDate && prefs.moveOutDate) || (prefs.moveInDate && !prefs.moveOutDate));

export const hasAnyPreference = (prefs: RenterPreferences): boolean =>
  !!(
    prefs.budgetMin ||
    prefs.budgetMax ||
    (prefs.preferredAreas && prefs.preferredAreas.length) ||
    (prefs.preferredTypes && prefs.preferredTypes.length) ||
    prefs.preferredBedrooms ||
    prefs.preferredBathrooms ||
    (prefs.preferredAmenities && prefs.preferredAmenities.length) ||
    (prefs.preferredServices && prefs.preferredServices.length) ||
    prefs.moveInDate ||
    prefs.moveOutDate ||
    prefs.numPeople ||
    prefs.hasPets
  );

// Returns true if the listing violates any of the renter's deal-breakers.
//
// Token format:
//   scalar : 'budget' | 'bedrooms' | 'bathrooms' | 'dates' | 'pets' | 'guests'
//   item   : 'area:<Area>' | 'type:<type>' | 'amenity:<Label>' | 'service:<Label>'
// area/type item tokens are OR'd (match one of the locked values); amenity/
// service item tokens are AND'd (the listing must have each).
const failsDealbreakers = (p: Property, prefs: RenterPreferences): boolean => {
  const db = prefs.dealbreakers;
  if (!db || db.length === 0) return false;

  if (db.includes("budget") && (prefs.budgetMin || prefs.budgetMax) && !budgetPass(p, prefs)) return true;
  if (db.includes("bedrooms") && prefs.preferredBedrooms && !(p.bedrooms >= prefs.preferredBedrooms)) return true;
  if (db.includes("bathrooms") && prefs.preferredBathrooms && !(p.bathrooms >= prefs.preferredBathrooms)) return true;
  if (db.includes("guests") && prefs.numPeople && p.maxGuests && !(p.maxGuests >= prefs.numPeople)) return true;
  if (db.includes("pets") && prefs.hasPets && !p.petFriendly) return true;
  if (db.includes("dates") && hasDatePref(prefs) && datesFraction(p, prefs) < 1) return true;

  const lockedAreas = db.filter((t) => t.startsWith("area:")).map((t) => t.slice(5));
  if (lockedAreas.length && !areaPass(p, lockedAreas)) return true;

  const lockedTypes = db.filter((t) => t.startsWith("type:")).map((t) => t.slice(5));
  if (lockedTypes.length && !typePass(p, lockedTypes)) return true;

  const lockedAmenities = db.filter((t) => t.startsWith("amenity:")).map((t) => t.slice(8));
  if (lockedAmenities.length) {
    const propA = propertyAmenitiesLower(p);
    if (!lockedAmenities.every((a) => propA.includes(a.toLowerCase()))) return true;
  }

  const lockedServices = db.filter((t) => t.startsWith("service:")).map((t) => t.slice(8));
  if (lockedServices.length && !lockedServices.every((s) => hasService(p, s))) return true;

  return false;
};

export const scoreProperty = (
  p: Property,
  prefs: RenterPreferences,
  weights: MatchWeights = DEFAULT_WEIGHTS,
): number => {
  const entries: Array<{ w: number; f: number }> = [];

  if (prefs.budgetMin || prefs.budgetMax) {
    entries.push({ w: weights.budget, f: budgetPass(p, prefs) ? 1 : 0 });
  }
  if (prefs.preferredAreas && prefs.preferredAreas.length > 0) {
    entries.push({ w: weights.area, f: areaPass(p, prefs.preferredAreas) ? 1 : 0 });
  }
  if (prefs.preferredTypes && prefs.preferredTypes.length > 0) {
    entries.push({ w: weights.type, f: typePass(p, prefs.preferredTypes) ? 1 : 0 });
  }
  if (prefs.preferredBedrooms) {
    entries.push({ w: weights.bedrooms, f: p.bedrooms >= prefs.preferredBedrooms ? 1 : 0 });
  }
  if (prefs.preferredBathrooms) {
    entries.push({ w: weights.bathrooms, f: p.bathrooms >= prefs.preferredBathrooms ? 1 : 0 });
  }
  if (prefs.numPeople && p.maxGuests) {
    entries.push({ w: weights.guests, f: p.maxGuests >= prefs.numPeople ? 1 : 0 });
  }
  if (prefs.hasPets) {
    entries.push({ w: weights.pets, f: p.petFriendly ? 1 : 0 });
  }
  if (prefs.preferredAmenities && prefs.preferredAmenities.length > 0) {
    const propA = propertyAmenitiesLower(p);
    const matched = prefs.preferredAmenities.filter((a) => propA.includes(a.toLowerCase())).length;
    entries.push({ w: weights.amenities, f: matched / prefs.preferredAmenities.length });
  }
  if (prefs.preferredServices && prefs.preferredServices.length > 0) {
    const matched = prefs.preferredServices.filter((s) => hasService(p, s)).length;
    entries.push({ w: weights.services, f: matched / prefs.preferredServices.length });
  }
  if (hasDatePref(prefs)) {
    entries.push({ w: weights.dates, f: datesFraction(p, prefs) });
  }

  if (entries.length === 0) return 0;
  const den = entries.reduce((s, e) => s + e.w, 0);
  if (den <= 0) return 0;

  // A failed deal-breaker hard-excludes the listing.
  if (failsDealbreakers(p, prefs)) return 0;

  const num = entries.reduce((s, e) => s + e.w * e.f, 0);
  return Math.round((num / den) * 100);
};

export interface MatchCriterion {
  label: string;
  matched: boolean;
  detail?: string;
  /** True when the renter marked this preference (or item) as a deal-breaker. */
  required?: boolean;
}

/**
 * Returns a per-criterion breakdown showing which of the renter's
 * preferences this property matches. Mirrors `scoreProperty`'s pass tests
 * and flags deal-breakers so the UI can mark must-haves.
 */
export const getMatchBreakdown = (p: Property, prefs: RenterPreferences): MatchCriterion[] => {
  const items: MatchCriterion[] = [];
  const db = prefs.dealbreakers ?? [];
  const isReq = (token: string) => db.includes(token);

  if (prefs.budgetMin || prefs.budgetMax) {
    const range =
      prefs.budgetMin && prefs.budgetMax
        ? `$${prefs.budgetMin.toLocaleString()}–$${prefs.budgetMax.toLocaleString()}`
        : prefs.budgetMin
        ? `min $${prefs.budgetMin.toLocaleString()}`
        : `max $${prefs.budgetMax!.toLocaleString()}`;
    items.push({
      label: "Budget",
      matched: budgetPass(p, prefs),
      detail: `Your range: ${range} · Listed: $${p.price.toLocaleString()}/mo`,
      required: isReq("budget"),
    });
  }

  if (prefs.preferredAreas && prefs.preferredAreas.length > 0) {
    items.push({
      label: "Area",
      matched: areaPass(p, prefs.preferredAreas),
      detail: `Your areas: ${prefs.preferredAreas.join(", ")} · Listed: ${p.location}`,
      required: prefs.preferredAreas.some((a) => isReq(`area:${a}`)),
    });
  }

  if (prefs.preferredTypes && prefs.preferredTypes.length > 0) {
    items.push({
      label: "Property type",
      matched: typePass(p, prefs.preferredTypes),
      detail: `Your types: ${prefs.preferredTypes.join(", ")} · Listed: ${p.type}`,
      required: prefs.preferredTypes.some((t) => isReq(`type:${t}`)),
    });
  }

  if (prefs.preferredBedrooms) {
    items.push({
      label: "Bedrooms",
      matched: p.bedrooms >= prefs.preferredBedrooms,
      detail: `Need ${prefs.preferredBedrooms}+ · Listed: ${p.bedrooms}`,
      required: isReq("bedrooms"),
    });
  }

  if (prefs.preferredBathrooms) {
    items.push({
      label: "Bathrooms",
      matched: p.bathrooms >= prefs.preferredBathrooms,
      detail: `Need ${prefs.preferredBathrooms}+ · Listed: ${p.bathrooms}`,
      required: isReq("bathrooms"),
    });
  }

  if (prefs.numPeople && p.maxGuests) {
    items.push({
      label: "Guests",
      matched: p.maxGuests >= prefs.numPeople,
      detail: `Need ${prefs.numPeople} · Sleeps ${p.maxGuests}`,
      required: isReq("guests"),
    });
  }

  if (prefs.hasPets) {
    items.push({
      label: "Pet friendly",
      matched: !!p.petFriendly,
      detail: p.petFriendly ? "Pets allowed" : "Pets not allowed",
      required: isReq("pets"),
    });
  }

  if (prefs.preferredAmenities && prefs.preferredAmenities.length > 0) {
    const propA = propertyAmenitiesLower(p);
    prefs.preferredAmenities.forEach((a) => {
      items.push({
        label: a,
        matched: propA.includes(a.toLowerCase()),
        required: isReq(`amenity:${a}`),
      });
    });
  }

  if (prefs.preferredServices && prefs.preferredServices.length > 0) {
    prefs.preferredServices.forEach((s) => {
      items.push({
        label: `${s} included`,
        matched: hasService(p, s),
        required: isReq(`service:${s}`),
      });
    });
  }

  if (prefs.moveInDate && prefs.moveOutDate) {
    const months = requestedMonths(prefs);
    const propMonths = (p.available || []) as string[];
    let matched = false;
    let detail = "";
    if (propMonths.length > 0) {
      const exact = months.filter((m) => propMonths.includes(m)).length;
      matched = exact === months.length;
      detail = matched
        ? "All requested months available"
        : exact > 0
        ? `${exact} of ${months.length} months available`
        : "Requested months not available";
    } else if (p.availableDate) {
      matched = prefs.moveInDate >= parseISO(p.availableDate);
      detail = `Available from ${p.availableDate}`;
    }
    items.push({ label: "Dates", matched, detail, required: isReq("dates") });
  } else if (prefs.moveInDate && p.availableDate) {
    items.push({
      label: "Move-in date",
      matched: prefs.moveInDate >= parseISO(p.availableDate),
      detail: `Available from ${p.availableDate}`,
      required: isReq("dates"),
    });
  }

  return items;
};

/**
 * Match-score colours, one scale everywhere (Oct 13: the score showed in
 * light blue and was hard to read; the brand colour is terracotta). Strong:
 * solid dark terracotta; fair: the light tint with dark terracotta text;
 * weak: neutral. Listing scores use 80/50; how well a renter fits a home
 * (owner side) keeps its own 70/40.
 */
export const matchScoreClasses = (score: number, bands: { high: number; mid: number } = { high: 80, mid: 50 }) =>
  score >= bands.high
    ? { badge: "bg-terracotta-dark text-white", box: "bg-terracotta-light border-terracotta-dark/25", number: "text-terracotta-dark" }
    : score >= bands.mid
      ? { badge: "bg-terracotta-light text-terracotta-dark", box: "bg-terracotta-light border-terracotta-dark/15", number: "text-terracotta-dark" }
      : { badge: "bg-muted text-muted-foreground", box: "bg-muted border-border", number: "text-foreground" };

/** The owner-side bands (a renter's fit for a home). */
export const RENTER_FIT_BANDS = { high: 70, mid: 40 };
